import { Reference } from '@approvalmax/types';
import { DropdownEditor, Field } from '@approvalmax/ui';
import { dataProviders } from 'modules/common';
import { domain } from 'modules/data';
import { memo, useCallback, useMemo } from 'react';

import { messages } from './NetSuiteCustomSearchFields.messages';
import { NetSuiteCustomSearchFieldsProps } from './NetSuiteCustomSearchFields.types';

export const NetSuiteCustomSearchFields = memo<NetSuiteCustomSearchFieldsProps>((props) => {
    const { params, integrationCode, disabled, integrationEnabled, isLineCustomFields, updateParameter } = props;

    const customFields = useMemo(() => {
        const customFieldsList =
            params.company?.fields.filter((field) => {
                if (field.systemPurpose === domain.FieldSystemPurpose.NetSuiteCustom) {
                    const scriptId = field.netSuiteField?.fieldScriptId;

                    return isLineCustomFields
                        ? scriptId?.startsWith('custcol')
                        : scriptId?.startsWith('custbody') || scriptId?.startsWith('cseg');
                }

                return false;
            }) || [];

        return [...customFieldsList].sort((field1, field2) => {
            if (
                field1.netSuiteField?.fieldScriptId.startsWith('cseg') &&
                !field2.netSuiteField?.fieldScriptId.startsWith('cseg')
            ) {
                return -1;
            }

            if (
                !field1.netSuiteField?.fieldScriptId.startsWith('cseg') &&
                field2.netSuiteField?.fieldScriptId.startsWith('cseg')
            ) {
                return 1;
            }

            const fieldOrder1 = field1.netSuiteField?.order ?? customFieldsList.length;
            const fieldOrder2 = field2.netSuiteField?.order ?? customFieldsList.length;

            return fieldOrder1 - fieldOrder2;
        });
    }, [params.company, isLineCustomFields]);

    const updateNetSuiteCustomFields = useMemo(() => updateParameter('netSuiteCustomFields'), [updateParameter]);

    const onCustomFieldValueChange = useCallback(
        (fieldId: string, newValue: Reference | null) => {
            const currentCustomFields = params.netSuiteCustomFields;

            if (newValue) {
                const hasCustomField = currentCustomFields.find((field) => field.fieldId === fieldId);

                if (hasCustomField) {
                    updateNetSuiteCustomFields(
                        currentCustomFields.map((field) => {
                            if (field.fieldId === fieldId) {
                                return {
                                    ...newValue,
                                    fieldId,
                                    valueRefId: newValue.id,
                                };
                            }

                            return field;
                        })
                    );
                } else {
                    updateNetSuiteCustomFields([
                        ...currentCustomFields,
                        {
                            ...newValue,
                            fieldId,
                            valueRefId: newValue.id,
                        },
                    ]);
                }
            } else {
                updateNetSuiteCustomFields(currentCustomFields.filter((field) => field.fieldId !== fieldId));
            }
        },
        [updateNetSuiteCustomFields, params]
    );

    return customFields?.map((field) => {
        const customField = params.netSuiteCustomFields.find((item) => item.fieldId === field.id);

        return (
            <Field key={field.id} title={field.netSuiteField?.fieldName || ''}>
                <dataProviders.FieldDataProvider field={field} integrationCode={integrationCode}>
                    <DropdownEditor
                        placeholder={messages.netSuiteCustomPlaceholder}
                        disabled={disabled || !integrationEnabled}
                        value={customField || null}
                        onChange={(newValue: Reference | null) => onCustomFieldValueChange(field.id, newValue)}
                    />
                </dataProviders.FieldDataProvider>
            </Field>
        );
    });
});

NetSuiteCustomSearchFields.displayName = 'NetSuiteCustomSearchFields';
